import React, { type ReactElement, useEffect, useState } from 'react'
import { CircularProgress, Divider, type Palette, Skeleton, Typography, useTheme } from '@mui/material'
import { StoreStatus } from 'common/utils/types'
import { ReactComponent as ProjectInfoIcon } from 'assets/projectInfoIcon.svg'
import tendersBackground from 'assets/tendersBackground.svg'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ReactComponent as DownloadIcon } from 'assets/downloadIcon.svg'
import { ReactComponent as AuctionIcon } from 'assets/auction.svg'
import { useParams } from 'react-router-dom'
import parse from 'html-react-parser'
import { downloadFile, downloadHTML, getFileUrl } from 'common/files/files.service'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import theme from 'common/utils/theme'
import { formatDate } from 'utils/functions'
import { fetchTenderProject } from 'features/bids/bid.service'
import { type Tender, type ProjectDescription } from 'features/bids/bid.types'
import VendorInfoPanel from 'features/vendor/vendor-profile/components/vendor-info-panel'

import TenderCard from './components/TenderCard'
import styles from './AcceptedBidDetails.module.scss'

interface RenderDescriptionProps {
  title: string
  content?: string
  palette: Palette
}
enum DescriptionType {
  General = 'general',
  Brief = 'brief',
  Beneficiary = 'beneficiary',
  Detailed = 'detailed',
  Features = 'features',
}
const getTitleFromEnum = (type: DescriptionType): string => {
  switch (type) {
    case DescriptionType.General:
      return 'Opis projekta'
    case DescriptionType.Brief:
      return 'Uvod u projekat'
    case DescriptionType.Beneficiary:
      return 'Opis kompanije'
    case DescriptionType.Detailed:
      return 'Detaljni opis projekta'
    case DescriptionType.Features:
      return 'Ključne karakteristike i funkcionalnosti'
    default:
      return ''
  }
}

const isGeneratedFile = (obj: any): obj is GeneratedFile => {
  return obj && typeof obj === 'object' && 'read' in obj
}
const RenderDescription = ({ title, content, palette }: RenderDescriptionProps): JSX.Element => {
  return (
    <>
      <Typography variant="title1" sx={{ marginTop: '24px', color: palette.white.main }}>
        {title}
      </Typography>
      <div className={styles.rteWrapper}>
        {content && !content.includes('<error>') ? (
          parse(content)
        ) : (
          <Typography variant="title5">Nema opisa</Typography>
        )}
      </div>
    </>
  )
}
const AcceptedBidDetails: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const { palette } = useTheme()
  const { bidId } = useParams()
  const { getBidListStatus, tenderProject, getTenderProjectStatus } = useAppSelector((state) => state.bid)
  const [fetchedContents, setFetchedContents] = useState<Record<
    keyof ProjectDescription,
    string | undefined
  > | null>(null)
  const [fetchedBrief, setFetchedBrief] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (bidId) {
      void dispatch(fetchTenderProject({ bidId }))
    }
  }, [])

  const handleRTEDownload = async (): Promise<void> => {
    if (tenderProject?.brief.read?.url) {
      try {
        const content = await downloadHTML(tenderProject?.brief.read?.url)
        setFetchedBrief(content)
      } catch (e) {
        console.error(e)
      }
    }
  }
  const handleAllFilesDownload = async (): Promise<void> => {
    const keysToDownload: Array<keyof ProjectDescription> = ['general', 'beneficiary', 'detailed', 'features']
    const newContents: Record<string, string | undefined> = {}

    for (const key of keysToDownload) {
      const file = tenderProject?.description[key] ?? null
      if (file && isGeneratedFile(file) && file.read?.url) {
        try {
          newContents[key] = await downloadHTML(file.read.url)
        } catch (e) {
          console.error(`Error downloading ${String(key)}:`, e)
          newContents[key] = '<error>'
        }
      }
    }

    setFetchedContents(newContents)
  }

  useEffect(() => {
    if (tenderProject?.description) {
      void handleAllFilesDownload()
    }
  }, [tenderProject?.description])

  useEffect(() => {
    if (tenderProject?.brief) {
      void handleRTEDownload()
    }
  }, [tenderProject?.brief])

  const handleDownloadDoc = async (file: GeneratedFile): Promise<void> => {
    setLoading(true)
    if (file.key) {
      try {
        const generatedFile = await getFileUrl({
          key: file.key,
          mode: 'r',
        })
        await downloadFile(GeneratedFileType.ProjectDescriptionDoc, generatedFile)
      } catch (error) {
        console.error('Error while handling download:', error)
      }
    }
    setLoading(false)
  }

  return tenderProject && tenderProject.id === bidId ? (
    <div className={styles.container}>
      <VendorInfoPanel
        title="Informacije o projektu"
        icon={<ProjectInfoIcon fill={palette.green.main} />}
        contentClassName={styles.documents}
        rightHeader={
          <>
            <>
              <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                Rok za davanje ponude{' '}
              </Typography>
              <Typography variant="subtitle1">
                {tenderProject?.deadline?.proposal
                  ? formatDate(new Date(tenderProject?.deadline?.proposal))
                  : '-'}
              </Typography>
            </>
            <>
              <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                Kraj tendera{' '}
              </Typography>
              <Typography variant="subtitle1">
                {' '}
                {tenderProject.deadline?.resolution
                  ? formatDate(new Date(tenderProject.deadline?.resolution))
                  : '-'}
              </Typography>
            </>
          </>
        }
        rightHeaderClassName={styles.rightHeader}
      >
        <div
          id="bidListWrapper"
          className={styles.bidListWrapper}
          style={{
            backgroundImage: `url(${tendersBackground})`,
            backgroundPosition: 'right bottom',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <Typography
            variant="title1"
            sx={{ paddingLeft: '20px', paddingBottom: '24px', color: palette.white.main }}
          >
            {tenderProject.name.toUpperCase()}
          </Typography>
          <Divider className={styles.divider} flexItem />
          <div className={styles.project}>
            <div className={styles.descriptions}>
              {Object.values(DescriptionType).map((type) => {
                let content
                if (type === DescriptionType.Brief) {
                  content = fetchedBrief
                } else {
                  content = fetchedContents?.[type]
                }
                return (
                  <RenderDescription
                    key={type}
                    title={getTitleFromEnum(type)}
                    content={content}
                    palette={palette}
                  />
                )
              })}
            </div>
            <div className={styles.document}>
              <Typography variant="subtitle1">O našem programu</Typography>
              <div className={styles.documentContent}>
                <Typography variant="subtitle1" sx={{ color: palette.white.main }}>
                  {tenderProject.brief.filename}
                </Typography>
                <div
                  onClick={() => {
                    void handleDownloadDoc(tenderProject.brief)
                  }}
                  className={styles.iconContainer}
                >
                  {loading ? (
                    <div className={styles.loaderDoc}>
                      <CircularProgress size={24} />
                    </div>
                  ) : (
                    <DownloadIcon height="20px" width="20px" fill={palette.blue100.main} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </VendorInfoPanel>
      <VendorInfoPanel
        title="Dostupni tenderi za ovaj projekat"
        icon={<AuctionIcon fill={theme.palette.green.main} />}
        contentClassName={styles.tenderDocuments}
      >
        <div className={styles.tender}>
          {tenderProject.tenders.map((tender: Tender, index: number) => (
            <TenderCard
              key={tender.id}
              tender={tender}
              index={index + 1}
              proposalDeadline={tenderProject.deadline.proposal}
            />
          ))}
        </div>
      </VendorInfoPanel>
      {getBidListStatus === StoreStatus.Loading && (
        <div className={styles.loader}>
          <CircularProgress />
        </div>
      )}
    </div>
  ) : getTenderProjectStatus === StoreStatus.Loading ? (
    <div className={styles.skeleton}>
      <Skeleton height={500} width="100%" variant="rounded" sx={{ backgroundColor: palette.blue600.main }} />
      <Skeleton height={300} width="100%" variant="rounded" sx={{ backgroundColor: palette.blue600.main }} />
    </div>
  ) : (
    <div className={styles.emptyState}>
      <Typography>Nema podataka</Typography>
    </div>
  )
}

export default AcceptedBidDetails
